import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';
import useScrollToTop from '@/hooks/useScrollToTop';
import { ComicCard } from '@/pages/home/components/HomeSections';
import { allComics, categories, categorySlugs } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function CategoryPage() {
  useScrollToTop();
  const { slug } = useParams<{ slug: string }>();
  const [filteredComics, setFilteredComics] = useState<Comic[]>([]);
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'views'>('latest');

  const currentSlug = slug || 'all';
  const cat = categories[currentSlug];

  useEffect(() => {
    if (!cat) return;
    document.title = `${cat.name}韩漫 - 虚空韩漫网 | 韩漫在线阅读`;
  }, [cat]);

  useEffect(() => {
    let comics = currentSlug === 'all' ? [...allComics] : allComics.filter((c) => c.category === currentSlug);
    if (sortBy === 'latest') {
      comics.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    } else if (sortBy === 'rating') {
      comics.sort((a, b) => b.rating - a.rating);
    } else {
      comics.sort((a, b) => b.views - a.views);
    }
    setFilteredComics(comics);
  }, [currentSlug, sortBy]);

  if (!cat) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="font-heading font-bold text-xl text-foreground-950 mb-2">分类不存在</h2>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm whitespace-nowrap cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SEOSchema type="Organization" />
      <SEOSchema type="WebSite" />
      <SEOSchema type="WebPage" data={{
        name: `${cat.name}韩漫 - 虚空韩漫网`,
        description: `虚空韩漫网${cat.name}韩漫分类，${cat.description}，免费在线阅读无删减下拉式韩国漫画`,
        url: `http://0661yes.cn/category/${currentSlug}`,
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="bg-foreground-950 py-12 md:py-16">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
              <p className="text-foreground-400 text-xs font-medium tracking-wider uppercase mb-3">漫画分类</p>
              <h1 className="font-heading font-black text-3xl md:text-4xl text-white">{cat.name}韩漫</h1>
              <p className="text-foreground-300 text-sm mt-3 max-w-lg mx-auto">{cat.description}，虚空韩漫网为您提供最全最新的{cat.name}类韩国漫画</p>
            </div>
          </section>

          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: `${cat.name}韩漫` },
            ]} />
          </div>

          <section className="w-full px-4 md:px-6 max-w-7xl mx-auto pb-4">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 overflow-x-auto flex-wrap">
              {categorySlugs.map((s) => {
                const c = categories[s];
                return (
                  <Link
                    key={s}
                    to={`/category/${s}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${currentSlug === s ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'}`}
                  >
                    {c.name}
                  </Link>
                );
              })}
            </div>
          </section>

          <section className="w-full px-4 md:px-6 max-w-7xl mx-auto pb-6">
            <div className="flex items-center justify-between">
              <p className="text-foreground-500 text-xs">共 {filteredComics.length} 部作品</p>
              <div className="flex items-center gap-1 bg-background-100 rounded-lg p-0.5">
                {(['latest', 'rating', 'views'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setSortBy(s)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${sortBy === s ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'}`}
                  >
                    {s === 'latest' ? '最新' : s === 'rating' ? '评分' : '人气'}
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 max-w-7xl mx-auto pb-14">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
            {filteredComics.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-file-search-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类下暂无漫画</p>
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}